// RECORD-TYPES-1 — a business invents a record type Ghost has never had.
//
// THIS FILE EXISTS TWICE, BYTE FOR BYTE:
//   src/lib/recordTypes.ts                        (the app renders from it)
//   supabase/functions/_shared/recordTypes.ts     (the read door merges it)
// Deno cannot import from src/ and Vite cannot import from supabase/functions,
// which is why this repository mirrors rather than shares; both suites assert
// the two are identical. NO imports on purpose: nothing to diverge.
//
// THE STANDING RULE THIS WAVE ESTABLISHES: A RECORD TYPE GHOST CANNOT SEE MUST
// NEVER SHIP. A custom type the assistant is blind to makes Ghost dumber the
// more a business customises, which is backwards - the whole promise is that a
// business shapes the product and the agent inside it keeps up. So the
// definitions and the door's sight of them are one wave, and
// `entityDefForType` below is the single function that makes a merchant's
// invention legible to the door. It is exercised by both suites.
//
// THE SECOND RULE, INHERITED FROM CUSTOM-FIELDS-1: AN ACCOUNT WITH NO CUSTOM
// TYPES IS BYTE IDENTICAL. No types means no nav rows, no routes rendered, no
// registry entries, and a door whose entity list is exactly the eighteen it
// always had.

// ─── A type ────────────────────────────────────────────────────────────

export interface CustomRecordType {
  id: string
  /** STABLE IDENTITY. The url segment, the nav node id and the door's entity
   *  name are all derived from this, and it never changes once created. */
  slug: string
  /** The business's words. Renamable; nothing ever finds a type by them. */
  labelOne: string
  labelMany: string
  /** A lucide icon name. Display only, and validated against a known set so a
   *  typo renders a fallback rather than crashing a page. */
  icon: string
  displayOrder: number
  /** Soft removal. A removed type keeps its records. */
  removedAt: string | null
}

/** Same shape as a custom field key, and for the same reason: a slug can never
 *  be a path, a label, or anything that would need escaping in a url. */
export const RECORD_TYPE_SLUG_PATTERN = /^[a-z][a-z0-9_]{0,39}$/

export const RECORD_TYPE_LABEL_MAX = 40
/** Per account. A navigation menu is a menu, not a database browser. */
export const MAX_RECORD_TYPES = 24

/**
 * THE ICONS A TYPE MAY CARRY.
 *
 * A closed list, deliberately: the icon is rendered by looking the name up in
 * lucide's export map, and an arbitrary string would let a definition name an
 * export that is not an icon at all. Fifteen shapes that mean something
 * different from each other, so a business can tell its types apart at a
 * glance in the menu.
 */
export const RECORD_TYPE_ICONS = [
  'Box',
  'Boxes',
  'Building2',
  'Car',
  'ClipboardList',
  'FileText',
  'Folder',
  'Hammer',
  'HeartPulse',
  'Key',
  'MapPin',
  'Package',
  'Scale',
  'Truck',
  'Wrench',
] as const

export type RecordTypeIcon = (typeof RECORD_TYPE_ICONS)[number]
export const DEFAULT_RECORD_TYPE_ICON: RecordTypeIcon = 'Box'

export function isRecordTypeIcon(v: unknown): v is RecordTypeIcon {
  return typeof v === 'string' && (RECORD_TYPE_ICONS as readonly string[]).includes(v)
}

/** An icon we do not know renders the fallback rather than throwing. */
export function iconOf(type: Pick<CustomRecordType, 'icon'>): RecordTypeIcon {
  return isRecordTypeIcon(type.icon) ? type.icon : DEFAULT_RECORD_TYPE_ICON
}

// ─── A record ──────────────────────────────────────────────────────────

export interface CustomRecord {
  id: string
  typeId: string
  /** The merchant's own fields, keyed by custom field definition key —
   *  exactly the shape CUSTOM-FIELDS-1 stores on a built-in record. */
  values: Record<string, unknown>
  createdAt: string
  updatedAt: string
  removedAt: string | null
}

// ─── Links ─────────────────────────────────────────────────────────────

/**
 * WHAT A CUSTOM RECORD MAY RELATE TO, AND WHY EXACTLY THESE.
 *
 * The six built-in records are precisely CUSTOM_FIELD_RECORDS from
 * CUSTOM-FIELDS-1 - the records a business already extends. Reusing that list
 * rather than inventing a second one means there is ONE definition of "a
 * record a business shapes" in the product, not two that can drift. A test
 * asserts the two lists agree.
 *
 * Plus 'custom_record', so a type can relate to another type - a Property to
 * an Inspection, a Matter to a Filing. Without it a business could describe
 * half its world and not the half that connects.
 *
 * DELIBERATELY ABSENT: invoice and payment, for the reason CUSTOM-FIELDS-1
 * gave - money records and documents frozen at send. And team_member, because
 * "who is this assigned to" is an assignment, not a relationship, and giving
 * it a link row would create a second unindexed way to answer a question the
 * product already answers.
 */
export const LINK_TARGET_KINDS = [
  'customer',
  'lead',
  'job',
  'quote',
  'opportunity',
  'task',
  'custom_record',
] as const

export type LinkTargetKind = (typeof LINK_TARGET_KINDS)[number]

export function isLinkTargetKind(v: unknown): v is LinkTargetKind {
  return typeof v === 'string' && (LINK_TARGET_KINDS as readonly string[]).includes(v)
}

export interface CustomRecordLink {
  id: string
  recordId: string
  targetKind: LinkTargetKind
  targetId: string
}

// ─── The door ──────────────────────────────────────────────────────────
//
// GHOST-VISION law 1 is untouched by everything below. A custom type becomes
// an ordinary entity in the registry the door already merges: same caller
// credential, same forced sub_account_id, same explicit column allow-list, and
// no function gains a parameter. The ONE new idea is `fixedFilter`, because
// every custom type shares one physical table and must be pinned to its own
// rows.

/** The physical columns of `custom_records` the door may read. FIXED, and not
 *  merchant-authored: a business names FIELDS, and those live inside `values`.
 *  So no merchant string can ever become a column name here, which is why the
 *  denied-column scan stays meaningful for a type that did not exist when it
 *  was written. */
export const CUSTOM_RECORD_COLUMNS = [
  'id',
  'type_id',
  'values',
  'created_at',
  'updated_at',
  'removed_at',
] as const

export const CUSTOM_RECORD_TABLE = 'custom_records'

/** The entity name the door and the model use for a custom type. Prefixed so a
 *  merchant can never define a type that shadows a built-in entity: a slug of
 *  `job` becomes `custom_job`, and `job` still means the built-in one. */
export function entityNameForType(type: Pick<CustomRecordType, 'slug'>): string {
  return `custom_${type.slug}`
}

export const CUSTOM_ENTITY_PREFIX = 'custom_'

export function isCustomEntityName(name: string): boolean {
  return name.startsWith(CUSTOM_ENTITY_PREFIX)
}

/** The door's EntityDef shape, mirrored here so this file stays import-free.
 *  Structurally identical to EntityDef in servicebotCrm.ts; a test asserts the
 *  two agree field for field. */
export interface DoorEntityDef {
  table: string
  columns: string[]
  filterFields: string[]
  dateFilterFields: string[]
  sortFields: string[]
  defaultSort: { field: string; ascending: boolean }
  scope: 'sub_account' | 'conversation'
  archivedColumn?: string
  blurb: string
  /** RECORD-TYPES-1: every custom type lives in one table, so each is pinned
   *  to its own rows by an equality the door applies before any filter the
   *  model supplies. Not overridable and not filterable. */
  fixedFilter?: { column: string; value: string }
}

/**
 * TURN ONE ACCOUNT'S TYPE INTO A DOOR ENTITY.
 *
 * `fields` are that type's own custom field definitions, so the READABLE
 * surface of a custom type is described by what the business defined and by
 * nothing hardcoded. They are reported in the blurb - which is what the model
 * reads to know what it may ask for - while the SELECTED columns stay the six
 * physical ones, because the field values live inside `values`.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO: it does not make a custom field
 * filterable or sortable. Those live inside a jsonb document, and filtering
 * there needs query construction the door has no shape for - the same honest
 * limit CUSTOM-FIELDS-1 recorded for custom fields on built-in records.
 */
export function entityDefForType(
  type: CustomRecordType,
  fields: ReadonlyArray<{ key: string; label: string; type: string }>,
): DoorEntityDef {
  const described = fields.length
    ? ` Its fields, inside \`values\`: ${fields.map((f) => `${f.key} (${f.type})`).join(', ')}.`
    : ' It has no fields defined yet.'
  return {
    table: CUSTOM_RECORD_TABLE,
    columns: [...CUSTOM_RECORD_COLUMNS],
    // Nothing physical is worth filtering on: type_id is already pinned, and
    // id/timestamps are covered by the date filters below.
    filterFields: [],
    dateFilterFields: ['created_at', 'updated_at'],
    sortFields: ['created_at', 'updated_at'],
    defaultSort: { field: 'created_at', ascending: false },
    scope: 'sub_account',
    archivedColumn: 'removed_at',
    blurb: `${type.labelMany} — a record type this business defined.${described}`,
    fixedFilter: { column: 'type_id', value: type.id },
  }
}

/**
 * MERGE AN ACCOUNT'S TYPES INTO THE BUILT-IN REGISTRY.
 *
 * Built-ins win every collision, unconditionally. The `custom_` prefix already
 * makes a collision impossible, and this is the belt to that pair of braces:
 * if a future change ever removed the prefix, a merchant could not shadow
 * `job` and start receiving built-in reads under their own name.
 */
export function mergeAccountTypes(
  builtin: Record<string, DoorEntityDef>,
  types: ReadonlyArray<CustomRecordType>,
  fieldsByTypeId: Readonly<
    Record<string, ReadonlyArray<{ key: string; label: string; type: string }>>
  >,
): Record<string, DoorEntityDef> {
  const merged: Record<string, DoorEntityDef> = { ...builtin }
  for (const type of types) {
    if (type.removedAt) continue
    const name = entityNameForType(type)
    if (name in builtin) continue
    merged[name] = entityDefForType(type, fieldsByTypeId[type.id] ?? [])
  }
  return merged
}

// ─── Validation ────────────────────────────────────────────────────────

export interface TypeValidationError {
  ok: false
  reason: string
}

export function validateRecordType(
  draft: Pick<CustomRecordType, 'slug' | 'labelOne' | 'labelMany' | 'icon'>,
  existingSlugs: ReadonlyArray<string>,
): TypeValidationError | { ok: true } {
  if (!RECORD_TYPE_SLUG_PATTERN.test(draft.slug)) {
    return { ok: false, reason: 'A type key is lower case letters, numbers and underscores.' }
  }
  if (existingSlugs.includes(draft.slug)) {
    return { ok: false, reason: `You already have a type saved as ${draft.slug}.` }
  }
  const one = draft.labelOne.trim()
  const many = draft.labelMany.trim()
  if (!one || !many) return { ok: false, reason: 'Give the type a singular and a plural name.' }
  if (one.length > RECORD_TYPE_LABEL_MAX || many.length > RECORD_TYPE_LABEL_MAX) {
    return { ok: false, reason: `A name is at most ${RECORD_TYPE_LABEL_MAX} characters.` }
  }
  if (!isRecordTypeIcon(draft.icon)) return { ok: false, reason: 'Pick an icon.' }
  return { ok: true }
}

/** Turn a plural label into a suggested slug. The editor's convenience only:
 *  the slug is stored and never re-derived, exactly like a custom field key. */
export function suggestSlug(label: string): string {
  const base = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40)
  if (!base) return ''
  return /^[a-z]/.test(base) ? base : `t_${base}`.slice(0, 40)
}

// ─── Routes and nav ────────────────────────────────────────────────────

export const CUSTOM_RECORD_ROUTE_BASE = '/dashboard/records'

export function listPathForType(type: Pick<CustomRecordType, 'slug'>): string {
  return `${CUSTOM_RECORD_ROUTE_BASE}/${type.slug}`
}

export function detailPathForType(type: Pick<CustomRecordType, 'slug'>, recordId: string): string {
  return `${CUSTOM_RECORD_ROUTE_BASE}/${type.slug}/${recordId}`
}

/**
 * THE NAV NODE ID FOR A TYPE.
 *
 * PLATFORM-1 keys its per-account navigation layer on stable node ids, and its
 * whole lesson was that an id derived from anything mutable silently orphans.
 * So this derives from the SLUG, which never changes, and never from the
 * label, which a business renames freely. A business that hides or reorders
 * one of its own custom type rows keeps that decision across a rename.
 */
export function navNodeIdForType(type: Pick<CustomRecordType, 'slug'>): string {
  return `records_${type.slug}`
}

/** Active types, in the order the business chose. */
export function activeTypes(types: ReadonlyArray<CustomRecordType>): CustomRecordType[] {
  return types
    .filter((t) => !t.removedAt)
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder || a.slug.localeCompare(b.slug))
}

export function typeBySlug(
  types: ReadonlyArray<CustomRecordType>,
  slug: string | undefined,
): CustomRecordType | null {
  if (!slug) return null
  return types.find((t) => t.slug === slug) ?? null
}
